/**
 * PensionCalculatorUtil
 *
 * Fee tradeoff analysis for pension products.
 * Compares current vs proposed fee structures (contribution fee + asset/AUM fee)
 * and returns a decision: SWITCH / STAY / NEED_SIMULATION with supporting metrics.
 *
 * All calculations are "screening / pre-check" (year-1 snapshot), NOT a simulation.
 */

// ────────────────────── Types ──────────────────────

export interface FeeStructure {
  /** contribution fee rate, e.g. 0.02 = 2% */
  contribution_fee_rate: number;
  /** asset (AUM / savings) fee rate, e.g. 0.0009 = 0.09% */
  asset_fee_rate: number;
}

export interface ClientContext {
  /** current accumulated assets (NIS) */
  current_assets: number;
  /** monthly contribution (NIS) */
  monthly_contribution: number;
  // optional – kept for future simulation
  monthly_insurance_cost?: number;
  age?: number;
  retirement_age?: number;
}

export type Decision = "SWITCH" | "STAY" | "NEED_SIMULATION";
export type Confidence = "HIGH" | "MEDIUM";
export type ReasonCode =
  | "TRIVIAL_BOTH_BETTER"
  | "TRIVIAL_BOTH_WORSE"
  | "NO_CHANGE"
  | "ASSET_DOMINANT"
  | "CONTRIBUTION_DOMINANT"
  | "GREY_ZONE"
  | "ONLY_CONTRIBUTION_CHANGE"
  | "ONLY_ASSET_CHANGE";

export interface PerProductContrib {
  label: string;
  current_deposit_fee: number;
  proposed_deposit_fee: number;
  monthly_contribution: number;
  annual_gain: number;
}

export interface PerProductSaving {
  label: string;
  current_saving_fee: number;
  proposed_saving_fee: number;
  funds_sum: number;
  annual_loss: number;
}

export interface TradeoffMetrics {
  /** current accumulated assets (NIS) */
  current_assets: number;
  annual_contribution: number;
  /** A / C  — how many years of contributions are already accumulated */
  R_client: number;
  /** a1 - a0 (positive = proposed is more expensive on assets) */
  delta_a: number;
  /** f0 - f1 (positive = proposed is cheaper on contributions) */
  delta_f: number;
  /** delta_f * C */
  annual_gain_from_contrib: number;
  /** delta_a * A */
  annual_loss_from_assets: number;
  /** gain - loss */
  annual_net: number;
  /** (delta_f * C) / delta_a  — only when both deltas != 0 and have opposite effect */
  critical_assets: number | null;
  /** A / critical_assets */
  ratio_to_critical: number | null;
  is_tradeoff: boolean;
  is_trivial_case: boolean;
  /** פירוט חיסכון מהפקדה לכל מוצר בנפרד */
  per_product_contrib?: PerProductContrib[];
  /** פירוט עלות/חיסכון מצבירה לכל מוצר בנפרד */
  per_product_saving?: PerProductSaving[];
}

export interface DecisionResult {
  decision: Decision;
  confidence: Confidence;
  reason_code: ReasonCode;
  short_explanation: string;
  ui_bullets: BulletItem[];
}

export interface BulletItem {
  text: string;
  tooltip?: string;
}

export interface AlternativeResult {
  label: string;
  proposed_fee: FeeStructure;
  metrics: TradeoffMetrics;
  decision_result: DecisionResult;
}

export interface EvaluationSummary {
  alternatives: AlternativeResult[];
  best_candidate: AlternativeResult | null;
}

export interface ProductFeeInfo {
  label: string;
  deposit_fee: number;   // %
  saving_fee: number;    // %
  funds_sum: number;
  monthly_contribution: number;
}

export interface UnificationScenario {
  /** שם המוצר שדמי הניהול שלו ישמשו לאיחוד */
  source_label: string;
  unified_deposit_fee: number;   // %
  unified_saving_fee: number;    // %
  /** חיסכון שנתי נטו לעומת המדרגה המוצעת (חיובי = עדיף לאחד) */
  annual_net_vs_proposed: number;
  /** חיסכון שנתי נטו לעומת המצב הנוכחי (חיובי = עדיף לאחד) */
  annual_net_vs_current: number;
  per_product_contrib: PerProductContrib[];
  per_product_saving: PerProductSaving[];
}

export interface UnificationCheck {
  has_unification_opportunity: boolean;
  scenarios: UnificationScenario[];
  /** התרחיש הכדאי ביותר לאיחוד, אם קיים */
  best_scenario: UnificationScenario | null;
}

/**
 * בודק אם מדרגת האיחוד (השוואת דמי הניהול של שני המוצרים לדמי הניהול של אחד מהם)
 * עדיפה על המדרגה המוצעת.
 *
 * @param products שני המוצרים (מקיפה + משלימה) עם דמי הניהול הנוכחיים שלהם
 * @param proposedDepositFee דמי ניהול הפקדה מוצעים מהמדרגה (%)
 * @param proposedSavingFee  דמי ניהול צבירה מוצעים מהמדרגה (%)
 */
export function computeUnificationCheck(
  products: ProductFeeInfo[],
  proposedDepositFee: number | null,
  proposedSavingFee: number | null
): UnificationCheck {
  if (products.length < 2) {
    return { has_unification_opportunity: false, scenarios: [], best_scenario: null };
  }

  const totalFunds = products.reduce((s, p) => s + p.funds_sum, 0);
  const totalMonthly = products.reduce((s, p) => s + p.monthly_contribution, 0);

  // Current weighted fees
  const currentWeightedSaving = totalFunds > 0
    ? products.reduce((s, p) => s + p.saving_fee * p.funds_sum, 0) / totalFunds
    : products.reduce((s, p) => s + p.saving_fee, 0) / products.length;
  const currentWeightedDeposit = totalMonthly > 0
    ? products.reduce((s, p) => s + p.deposit_fee * p.monthly_contribution, 0) / totalMonthly
    : products.reduce((s, p) => s + p.deposit_fee, 0) / products.length;

  const effProposedDeposit = proposedDepositFee ?? currentWeightedDeposit;
  const effProposedSaving = proposedSavingFee ?? currentWeightedSaving;

  // Annual net under proposed tier (sum of per-product)
  const annualNetProposed = products.reduce((s, p) => {
    const gainFromContrib = (p.deposit_fee - effProposedDeposit) / 100 * p.monthly_contribution * 12;
    const lossFromAssets = (effProposedSaving - p.saving_fee) / 100 * p.funds_sum;
    return s + gainFromContrib - lossFromAssets;
  }, 0);

  // Annual net under current (baseline = 0)
  // For each unification scenario: take one product's deposit+saving fees and apply to all
  const scenarios: UnificationScenario[] = products.map(source => {
    const udf = source.deposit_fee;
    const usf = source.saving_fee;

    const perProductContrib: PerProductContrib[] = products
      .filter(p => p.deposit_fee !== udf)
      .map(p => ({
        label: p.label,
        current_deposit_fee: p.deposit_fee,
        proposed_deposit_fee: udf,
        monthly_contribution: p.monthly_contribution,
        annual_gain: (p.deposit_fee - udf) / 100 * p.monthly_contribution * 12,
      }));
    const perProductSaving: PerProductSaving[] = products
      .filter(p => p.saving_fee !== usf)
      .map(p => ({
        label: p.label,
        current_saving_fee: p.saving_fee,
        proposed_saving_fee: usf,
        funds_sum: p.funds_sum,
        annual_loss: (usf - p.saving_fee) / 100 * p.funds_sum,
      }));

    // Net vs current
    const annualNetVsCurrent =
      perProductContrib.reduce((s, p) => s + p.annual_gain, 0) -
      perProductSaving.reduce((s, p) => s + p.annual_loss, 0);

    // Net vs proposed tier
    const annualNetVsProposed = annualNetVsCurrent - annualNetProposed;

    return {
      source_label: source.label,
      unified_deposit_fee: udf,
      unified_saving_fee: usf,
      annual_net_vs_proposed: annualNetVsProposed,
      annual_net_vs_current: annualNetVsCurrent,
      per_product_contrib: perProductContrib,
      per_product_saving: perProductSaving,
    };
  });

  // Only keep scenarios that are better than the proposed tier
  const betterThanProposed = scenarios.filter(s => s.annual_net_vs_proposed > 0);

  const best = betterThanProposed.length > 0
    ? betterThanProposed.reduce((a, b) => a.annual_net_vs_proposed >= b.annual_net_vs_proposed ? a : b)
    : null;

  return {
    has_unification_opportunity: best !== null,
    scenarios,
    best_scenario: best,
  };
}

// ────────────────── Grey-zone band ──────────────────
const BAND_LOW = 0.8;
const BAND_HIGH = 1.2;

// ────────────────── Core functions ──────────────────

export function computeFeeTradeoffMetrics(
  ctx: ClientContext,
  current: FeeStructure,
  proposed: FeeStructure
): TradeoffMetrics {
  const A = ctx.current_assets;
  const C = ctx.monthly_contribution * 12;

  const delta_a = proposed.asset_fee_rate - current.asset_fee_rate;
  const delta_f = current.contribution_fee_rate - proposed.contribution_fee_rate;

  const annual_gain_from_contrib = delta_f * C;
  const annual_loss_from_assets = delta_a * A;
  const annual_net = annual_gain_from_contrib - annual_loss_from_assets;

  // tradeoff = both deltas same sign (one helps, one hurts)
  // delta_f>0 && delta_a>0: cheaper contribution but pricier assets
  // delta_f<0 && delta_a<0: pricier contribution but cheaper assets
  const is_tradeoff =
    delta_f !== 0 &&
    delta_a !== 0 &&
    Math.sign(delta_f) === Math.sign(delta_a);

  // trivial = both better or both worse (or equal)
  const is_trivial_case =
    (delta_f >= 0 && delta_a <= 0) || (delta_f <= 0 && delta_a >= 0);

  let critical_assets: number | null = null;
  let ratio_to_critical: number | null = null;

  if (is_tradeoff && delta_a !== 0) {
    critical_assets = (delta_f * C) / delta_a;
    if (critical_assets > 0) {
      ratio_to_critical = A / critical_assets;
    }
  }

  const R_client = C > 0 ? A / C : 0;

  return {
    current_assets: A,
    annual_contribution: C,
    R_client,
    delta_a,
    delta_f,
    annual_gain_from_contrib,
    annual_loss_from_assets,
    annual_net,
    critical_assets,
    ratio_to_critical,
    is_tradeoff,
    is_trivial_case,
  };
}

export function decideFeeSwitch(metrics: TradeoffMetrics): DecisionResult {
  const {
    delta_a,
    delta_f,
    annual_net,
    ratio_to_critical,
    is_tradeoff,
    annual_gain_from_contrib,
    annual_loss_from_assets,
    critical_assets,
  } = metrics;

  // ─── No change ───
  if (delta_f === 0 && delta_a === 0) {
    return {
      decision: "STAY",
      confidence: "HIGH",
      reason_code: "NO_CHANGE",
      short_explanation: "אין הבדל בדמי הניהול.",
      ui_bullets: [{ text: "דמי ניהול זהים, אין צורך במעבר." }],
    };
  }

  // ─── Trivial: both better ───
  if (delta_f >= 0 && delta_a <= 0 && (delta_f > 0 || delta_a < 0)) {
    return {
      decision: "SWITCH",
      confidence: "HIGH",
      reason_code: "TRIVIAL_BOTH_BETTER",
      short_explanation: "שני רכיבי דמי הניהול משתפרים או שווים.",
      ui_bullets: buildBullets(metrics),
    };
  }

  // ─── Trivial: both worse ───
  if (delta_f <= 0 && delta_a >= 0 && (delta_f < 0 || delta_a > 0)) {
    return {
      decision: "STAY",
      confidence: "HIGH",
      reason_code: "TRIVIAL_BOTH_WORSE",
      short_explanation: "שני רכיבי דמי הניהול מתייקרים או שווים.",
      ui_bullets: buildBullets(metrics),
    };
  }

  // ─── Only one dimension changed ───
  if (delta_a === 0) {
    return {
      decision: delta_f > 0 ? "SWITCH" : "STAY",
      confidence: "HIGH",
      reason_code: "ONLY_CONTRIBUTION_CHANGE",
      short_explanation:
        delta_f > 0
          ? "דמי ניהול מהפקדה נמוכים יותר, מצבירה זהים."
          : "דמי ניהול מהפקדה גבוהים יותר, מצבירה זהים.",
      ui_bullets: buildBullets(metrics),
    };
  }
  if (delta_f === 0) {
    return {
      decision: delta_a < 0 ? "SWITCH" : "STAY",
      confidence: "HIGH",
      reason_code: "ONLY_ASSET_CHANGE",
      short_explanation:
        delta_a < 0
          ? "דמי ניהול מצבירה נמוכים יותר, מהפקדה זהים."
          : "דמי ניהול מצבירה גבוהים יותר, מהפקדה זהים.",
      ui_bullets: buildBullets(metrics),
    };
  }

  // ─── Tradeoff with band ───
  if (is_tradeoff && ratio_to_critical !== null) {
    const isHighConfidence = ratio_to_critical <= BAND_LOW || ratio_to_critical >= BAND_HIGH;
    const confidence: Confidence = isHighConfidence ? "HIGH" : "MEDIUM";

    if (!isHighConfidence) {
      return {
        decision: "NEED_SIMULATION",
        confidence: "MEDIUM",
        reason_code: "GREY_ZONE",
        short_explanation:
          "החלופה נמצאת בתחום אפור (קרוב לנקודת האיזון). נדרשת סימולציה.",
        ui_bullets: buildBullets(metrics),
      };
    }

    // Determine saving source and cost source based on actual deltas
    const savingSource = delta_f > 0 ? "הפקדה" : "צבירה";
    const costSource = delta_a > 0 ? "צבירה" : "הפקדה";

    const fmt = (n: number) => Math.round(n).toLocaleString();

    if (annual_net >= 0) {
      const isWellAboveCritical = ratio_to_critical >= BAND_HIGH;
      const explanation = isWellAboveCritical
        ? `הוזלת דמי הניהול מ${savingSource} פועלת על צבירה גבוהה משמעותית מנקודת האיזון ולכן צפויה לגבור על התייקרות דמי הניהול מ${costSource}.`
        : `החיסכון מ${savingSource} דומיננטי ביחס להתייקרות מ${costSource}.`;
      const bullets = buildBullets(metrics);
      if (isWellAboveCritical) {
        bullets.push({ text: `כבר בשנה הראשונה צפוי חיסכון של כ־${fmt(annual_net)} ₪, והפער צפוי לגדול עם הזמן.` });
      }
      return {
        decision: "SWITCH",
        confidence,
        reason_code: delta_f > 0 ? "CONTRIBUTION_DOMINANT" : "ASSET_DOMINANT",
        short_explanation: explanation,
        ui_bullets: bullets,
      };
    } else {
      return {
        decision: "STAY",
        confidence,
        reason_code: delta_a > 0 ? "ASSET_DOMINANT" : "CONTRIBUTION_DOMINANT",
        short_explanation: `ההתייקרות מ${costSource} דומיננטית ביחס לחיסכון מ${savingSource}.`,
        ui_bullets: buildBullets(metrics),
      };
    }
  }

  // Fallback (shouldn't normally reach here)
  return {
    decision: annual_net >= 0 ? "SWITCH" : "STAY",
    confidence: "MEDIUM",
    reason_code: annual_net >= 0 ? "CONTRIBUTION_DOMINANT" : "ASSET_DOMINANT",
    short_explanation:
      annual_net >= 0
        ? "על בסיס שנה ראשונה, המעבר צפוי להיות כדאי."
        : "על בסיס שנה ראשונה, עדיף להישאר.",
    ui_bullets: buildBullets(metrics),
  };
}

export function evaluateAlternatives(
  ctx: ClientContext,
  currentFee: FeeStructure,
  alternatives: { label: string; fee: FeeStructure }[]
): EvaluationSummary {
  const results: AlternativeResult[] = alternatives.map((alt) => {
    const metrics = computeFeeTradeoffMetrics(ctx, currentFee, alt.fee);
    const decision_result = decideFeeSwitch(metrics);
    return {
      label: alt.label,
      proposed_fee: alt.fee,
      metrics,
      decision_result,
    };
  });

  // Best candidate logic
  const switchHigh = results.filter(
    (r) => r.decision_result.decision === "SWITCH" && r.decision_result.confidence === "HIGH"
  );
  let best: AlternativeResult | null = null;

  if (switchHigh.length > 0) {
    best = switchHigh.reduce((a, b) =>
      a.metrics.annual_net >= b.metrics.annual_net ? a : b
    );
  } else {
    const simCandidates = results.filter(
      (r) => r.decision_result.decision === "NEED_SIMULATION"
    );
    if (simCandidates.length > 0) {
      best = simCandidates.reduce((a, b) =>
        a.metrics.annual_net >= b.metrics.annual_net ? a : b
      );
    }
  }

  return { alternatives: results, best_candidate: best };
}

// ────────────────── Helpers ──────────────────

function buildBullets(m: TradeoffMetrics): BulletItem[] {
  const fmt = (n: number) => Math.round(n).toLocaleString();
  const fmtPct = (n: number) => (n * 100).toFixed(2) + "%";
  const bullets: BulletItem[] = [];

  bullets.push({
    text: `הפקדה חודשית: ${fmt(m.annual_contribution / 12)} ₪, הפקדה שנתית: ${fmt(m.annual_contribution)} ₪`,
  });
  bullets.push({
    text: `צבירה: ${fmt(m.current_assets)} ₪ (יחס: ${m.R_client.toFixed(1)})`,
  });

  if (m.delta_f !== 0 || (m.per_product_contrib && m.per_product_contrib.some(p => p.annual_gain !== 0))) {
    const dir = m.annual_gain_from_contrib >= 0 ? "חיסכון" : "תוספת";
    let tooltip: string;
    if (m.per_product_contrib && m.per_product_contrib.length > 1) {
      const lines = m.per_product_contrib.map(p =>
        `${p.label}: (${fmtPct((p.current_deposit_fee - p.proposed_deposit_fee) / 100)}) × ${fmt(p.monthly_contribution * 12)} = ${fmt(p.annual_gain)} ₪`
      );
      tooltip = lines.join("\n") + `\nסה"כ: ${fmt(m.annual_gain_from_contrib)} ₪`;
    } else {
      tooltip = `הפרש דמי ניהול מהפקדה (delta_f) = ${fmtPct(m.delta_f)}\nהפקדה שנתית = ${fmt(m.annual_contribution)} ₪\nחישוב: ${fmtPct(m.delta_f)} × ${fmt(m.annual_contribution)} = ${fmt(m.annual_gain_from_contrib)} ₪`;
    }
    bullets.push({
      text: `${dir} מהפקדה: ${fmt(Math.abs(m.annual_gain_from_contrib))} ₪ לשנה`,
      tooltip,
    });
  }
  if (m.delta_a !== 0) {
    const dir = m.annual_loss_from_assets > 0 ? "תוספת" : "חיסכון";
    let savingTooltip: string;
    if (m.per_product_saving && m.per_product_saving.length > 1) {
      const lines = m.per_product_saving.map(p =>
        `${p.label}: (${fmtPct((p.proposed_saving_fee - p.current_saving_fee) / 100)}) × ${fmt(p.funds_sum)} = ${fmt(p.annual_loss)} ₪`
      );
      savingTooltip = lines.join("\n") + `\nסה"כ: ${fmt(m.annual_loss_from_assets)} ₪`;
    } else {
      savingTooltip = `הפרש דמי ניהול מצבירה (delta_a) = ${fmtPct(m.delta_a)}\nצבירה = ${fmt(m.current_assets)} ₪\nחישוב: ${fmtPct(m.delta_a)} × ${fmt(m.current_assets)} = ${fmt(m.annual_loss_from_assets)} ₪`;
    }
    bullets.push({
      text: `${dir} מצבירה: ${fmt(Math.abs(m.annual_loss_from_assets))} ₪ לשנה`,
      tooltip: savingTooltip,
    });
  }

  if (m.critical_assets !== null && m.critical_assets > 0) {
    const position = m.ratio_to_critical !== null && m.ratio_to_critical < 1 ? "מתחת" : "מעל";
    const gainLabel = m.annual_gain_from_contrib >= 0 ? "החיסכון" : "העלות הנוספת";
    const lossLabel = m.annual_loss_from_assets >= 0 ? "התוספת" : "החיסכון";
    bullets.push({
      text: `נקודת איזון: ${fmt(m.critical_assets)} ₪ (הצבירה שלך ${position})`,
      tooltip:
        `נקודת האיזון היא הצבירה שבה ${gainLabel} מהפקדה שווה ל${lossLabel} מצבירה — כלומר ההחלטה הופכת כדאית.\n` +
        `\nחישוב:\n` +
        `  חיסכון שנתי מהפקדה = ${fmt(m.annual_gain_from_contrib)} ₪\n` +
        `  הפרש ד.נ צבירה (delta_a) = ${fmtPct(m.delta_a)}\n` +
        `  נקודת איזון = ${fmt(m.annual_gain_from_contrib)} ÷ ${fmtPct(m.delta_a)} = ${fmt(m.critical_assets)} ₪\n` +
        `\nצבירתך הנוכחית (${fmt(m.current_assets)} ₪) נמצאת ${position} נקודת האיזון.` +
        (m.ratio_to_critical != null ? `\nיחס: ${m.ratio_to_critical.toFixed(2)}` : ""),
    });
  }

  return bullets;
}
